import { tr } from '@/lib/i18n-core';
import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Download } from 'lucide-react';
import type { TrialBalanceRow } from '@his/shared';
import { get, put, qs } from '@/lib/api';
import { describeError, useAuth } from '@/lib/auth';
import { cn, dateOnly, humanize, money, todayIso, toIsoDate } from '@/lib/utils';
import { DataTable, FilterField, PageHeader, SearchFilterBar, StatTile, Tabs } from '@/components/ui/layout';
import { Badge, Button, Card, CardContent, CardHeader, CardTitle, DatePicker, Select } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';

export interface Account {
  id: string;
  code: string;
  name: string;
  type: string;
  isPostable: boolean;
  isActive: boolean;
}

interface JournalEntry {
  id: string;
  number: string;
  entryDate: string;
  memo: string | null;
  sourceType: string | null;
  status: string;
  lines: {
    id: string;
    debitMinor: number;
    creditMinor: number;
    memo: string | null;
    account: { code: string; name: string; type: string };
  }[];
}

export const CONTROL_ACCOUNT_CODES = ['1300', '1310', '1400', '2100', '2200', '2250'];

interface PeriodLock {
  lockedThrough: string | null;
  canMove: boolean;
}

function PeriodLockBar() {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [day, setDay] = useState('');
  const lock = useQuery({ queryKey: ['period-lock'], queryFn: () => get<PeriodLock>('/accounting/period-lock') });
  const save = useMutation({
    mutationFn: (lockedThrough: string | null) => put('/accounting/period-lock', { lockedThrough }),
    onSuccess: () => {
      toast.success(tr('billing', 'books_lock_updated'));
      setDay('');
      void queryClient.invalidateQueries({ queryKey: ['period-lock'] });
    },
    onError: (caught) => toast.error(tr('billing', 'could_not_change_the_lock'), describeError(caught)),
  });

  if (!lock.data) return null;
  return (
    <div className="mb-3 flex flex-wrap items-center gap-2 rounded-md border px-3 py-2 text-sm">
      <span>
        {tr('billing', 'books_locked_through')} <strong>{lock.data.lockedThrough ? dateOnly(lock.data.lockedThrough) : tr('billing', 'no_day_open')}</strong>
      </span>
      {lock.data.canMove && (
        <>
          <DatePicker value={day} max={todayIso()} onChange={setDay} className="h-8 w-40" />
          <Button size="sm" variant="outline" disabled={!day} loading={save.isPending} onClick={() => save.mutate(day)}>
            {tr('billing', 'lock_through_this_day')}
          </Button>
          {lock.data.lockedThrough && (
            <Button size="sm" variant="ghost" onClick={() => save.mutate(null)}>
              {tr('billing', 'open_the_books')}
            </Button>
          )}
        </>
      )}
    </div>
  );
}

const monthStart = () => {
  const now = new Date();
  return toIsoDate(new Date(now.getFullYear(), now.getMonth(), 1));
};

/**
 * Accounting (SRS 9.5).
 *
 * Postings from billing, pharmacy, claims and payroll arrive here on their own,
 * so the day-to-day use of this screen is reading rather than writing — which is
 * why the statements come first and the manual voucher is a dialog.
 */
const TABS = ['statements', 'journals', 'accounts'];

interface DateWindow {
  from: string;
  to: string;
  setFrom: (value: string) => void;
  setTo: (value: string) => void;
}

function useDateWindow(): DateWindow & { reset: (() => void) | undefined } {
  const [from, setFrom] = useState(monthStart());
  const [to, setTo] = useState(todayIso());
  const changed = from !== monthStart() || to !== todayIso();
  return {
    from,
    to,
    setFrom,
    setTo,
    reset: changed
      ? () => {
          setFrom(monthStart());
          setTo(todayIso());
        }
      : undefined,
  };
}

function DateWindowFields({ from, to, setFrom, setTo }: DateWindow) {
  return (
    <>
      <FilterField label={tr('billing', 'from')}>
        <DatePicker value={from} onChange={setFrom} className="w-40" />
      </FilterField>
      <FilterField label={tr('billing', 'to_2')}>
        <DatePicker value={to} onChange={setTo} className="w-40" />
      </FilterField>
    </>
  );
}

export function AccountingPage() {
  const { can } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [tab, setTab] = useState(() => {
    const wanted = params.get('tab');
    return wanted && TABS.includes(wanted) ? wanted : 'statements';
  });
  const dates = useDateWindow();

  return (
    <>
      <PageHeader
        title={tr('billing', 'accounting')}
        description={tr('billing', 'ledgers_statements_and_the_entries_behind_them')}
        actions={
          can('gl.manage') &&
          (tab === 'journals' ? (
            <Button onClick={() => navigate('/accounting/journals/new')}>{tr('billing', 'post_a_voucher')}</Button>
          ) : tab === 'accounts' ? (
            <Button onClick={() => navigate('/accounting/accounts/new')}>{tr('billing', 'add_account')}</Button>
          ) : null)
        }
      />
      <Tabs
        active={tab}
        onChange={setTab}
        tabs={[
          { id: 'statements', label: tr('billing', 'statements') },
          { id: 'journals', label: tr('billing', 'journals') },
          { id: 'accounts', label: tr('billing', 'chart_of_accounts') },
        ]}
      />
      {tab === 'statements' && <StatementsTab dates={dates} />}
      {tab === 'journals' && <JournalsTab dates={dates} />}
      {tab === 'accounts' && <AccountsTab />}
    </>
  );
}

function range(from: string, to: string) {
  return qs({
    from: new Date(`${from}T00:00:00.000Z`).toISOString(),
    to: new Date(`${to}T23:59:59.999Z`).toISOString(),
  });
}

function StatementsTab({ dates }: { dates: ReturnType<typeof useDateWindow> }) {
  const { from, to } = dates;
  const pnl = useQuery({
    queryKey: ['pnl', from, to],
    queryFn: () =>
      get<{
        income: TrialBalanceRow[];
        expense: TrialBalanceRow[];
        totalIncomeMinor: number;
        totalExpenseMinor: number;
        netProfitMinor: number;
      }>(`/accounting/reports/profit-and-loss${range(from, to)}`),
  });

  const trial = useQuery({
    queryKey: ['trial-balance', from, to],
    queryFn: () =>
      get<{ rows: TrialBalanceRow[]; totals: { debitMinor: number; creditMinor: number } }>(
        `/accounting/reports/trial-balance${range(from, to)}`,
      ),
  });

  const sheet = useQuery({
    queryKey: ['balance-sheet', to],
    queryFn: () =>
      get<{
        assets: TrialBalanceRow[];
        liabilities: TrialBalanceRow[];
        totalAssetsMinor: number;
        totalLiabilitiesMinor: number;
        retainedEarningsMinor: number;
        totalEquityMinor: number;
        differenceMinor: number;
      }>(`/accounting/reports/balance-sheet?asAt=${new Date(`${to}T23:59:59.999Z`).toISOString()}`),
  });

  const departments = useQuery({
    queryKey: ['dept-pnl', from, to],
    queryFn: () =>
      get<{ departmentId: string; departmentName: string; incomeMinor: number; expenseMinor: number; netMinor: number }[]>(
        `/accounting/reports/department-pnl${range(from, to)}`,
      ),
    enabled: Date.parse(`${to}T00:00:00.000Z`) - Date.parse(`${from}T00:00:00.000Z`) < 366 * 86_400_000,
  });

  return (
    <div className="space-y-4">
      <SearchFilterBar className="mb-0" filters={<DateWindowFields {...dates} />} onReset={dates.reset} />
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <StatTile label={tr('billing', 'income')} value={money(pnl.data?.totalIncomeMinor)} tone="success" />
        <StatTile label={tr('billing', 'expenses')} value={money(pnl.data?.totalExpenseMinor)} />
        <StatTile
          label={tr('billing', 'net')}
          value={money(pnl.data?.netProfitMinor)}
          tone={(pnl.data?.netProfitMinor ?? 0) >= 0 ? 'success' : 'destructive'}
        />
        <StatTile
          label={tr('billing', 'books_balance')}
          value={sheet.data?.differenceMinor === 0 ? 'Yes' : money(sheet.data?.differenceMinor)}
          tone={sheet.data?.differenceMinor === 0 ? 'success' : 'destructive'}
          sub={sheet.data?.differenceMinor === 0 ? tr('billing', 'assets_liabilities_equity') : tr('billing', 'investigate_before_closing')}
        />
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">{tr('billing', 'profit_and_loss')}</CardTitle>
          </CardHeader>
          <CardContent>
            <DataTable
              dense
              rows={[...(pnl.data?.income ?? []), ...(pnl.data?.expense ?? [])]}
              rowKey={(row) => row.accountId}
              empty={tr('billing', 'nothing_posted_in_this_window')}
              columns={[
                { header: tr('billing', 'account'), sort: (row) => row.code, cell: (row) => `${row.code} · ${row.name}` },
                { header: tr('billing', 'type'), sort: (row) => row.type, cell: (row) => <Badge variant={row.type === 'income' ? 'success' : 'secondary'}>{humanize(row.type, 'account_type')}</Badge> },
                { header: tr('billing', 'amount'), align: 'right', sort: (row) => row.balanceMinor, cell: (row) => money(row.balanceMinor) },
              ]}
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-sm">{tr('billing', 'balance_sheet')}</CardTitle>
          </CardHeader>
          <CardContent>
            <DataTable
              dense
              rows={[...(sheet.data?.assets ?? []), ...(sheet.data?.liabilities ?? [])]}
              rowKey={(row) => row.accountId}
              empty={tr('billing', 'nothing_on_the_sheet_yet')}
              columns={[
                { header: tr('billing', 'account'), sort: (row) => row.code, cell: (row) => `${row.code} · ${row.name}` },
                { header: tr('billing', 'type'), sort: (row) => row.type, cell: (row) => <Badge variant="secondary">{humanize(row.type, 'account_type')}</Badge> },
                { header: tr('billing', 'balance'), align: 'right', sort: (row) => row.balanceMinor, cell: (row) => money(row.balanceMinor) },
              ]}
            />
            <div className="mt-3 space-y-1 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">{tr('billing', 'retained_earnings')}</span>
                <span className="tabular">{money(sheet.data?.retainedEarningsMinor)}</span>
              </div>
              <div className="flex justify-between font-medium">
                <span>{tr('billing', 'total_equity')}</span>
                <span className="tabular">{money(sheet.data?.totalEquityMinor)}</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-sm">{tr('billing', 'trial_balance')}</CardTitle>
        </CardHeader>
        <CardContent>
          <DataTable
            dense
            rows={trial.data?.rows}
            rowKey={(row) => row.accountId}
            empty={tr('billing', 'no_postings_in_this_window')}
            columns={[
              { header: tr('billing', 'code'), sort: (row) => row.code, cell: (row) => <span className="font-mono text-xs">{row.code}</span> },
              { header: tr('billing', 'account'), sort: (row) => row.name, cell: (row) => row.name },
              { header: tr('billing', 'debit'), align: 'right', sort: (row) => row.debitMinor, cell: (row) => money(row.debitMinor) },
              { header: tr('billing', 'credit'), align: 'right', sort: (row) => row.creditMinor, cell: (row) => money(row.creditMinor) },
            ]}
          />
          {trial.data && (
            <p
              className={cn(
                'mt-2 text-end text-sm font-medium tabular',
                trial.data.totals.debitMinor === trial.data.totals.creditMinor ? 'text-success' : 'text-destructive',
              )}
            >
              {money(trial.data.totals.debitMinor)} {tr('billing', 'debit_2')} {money(trial.data.totals.creditMinor)} {tr('billing', 'credit_2')}
            </p>
          )}
        </CardContent>
      </Card>

      {(departments.data?.length ?? 0) > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">{tr('billing', 'department_p_and_l')}</CardTitle>
          </CardHeader>
          <CardContent>
            <DataTable
              dense
              rows={departments.data}
              rowKey={(row) => row.departmentId}
              columns={[
                { header: tr('billing', 'department'), sort: (row) => row.departmentName, cell: (row) => row.departmentName },
                { header: tr('billing', 'income'), align: 'right', sort: (row) => row.incomeMinor, cell: (row) => money(row.incomeMinor) },
                { header: tr('billing', 'expense'), align: 'right', sort: (row) => row.expenseMinor, cell: (row) => money(row.expenseMinor) },
                { header: tr('billing', 'net'), align: 'right', sort: (row) => row.netMinor, cell: (row) => money(row.netMinor) },
              ]}
            />
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function JournalsTab({ dates }: { dates: ReturnType<typeof useDateWindow> }) {
  const { from, to } = dates;
  const [sourceType, setSourceType] = useState('');

  const journals = useQuery({
    queryKey: ['journals', from, to, sourceType],
    queryFn: () =>
      get<JournalEntry[]>(
        `/accounting/journals${range(from, to)}&limit=200${sourceType ? `&sourceType=${sourceType}` : ''}`,
      ),
  });

  const reset =
    dates.reset || sourceType
      ? () => {
          dates.reset?.();
          setSourceType('');
        }
      : undefined;

  return (
    <>
      <SearchFilterBar
        filters={
          <>
            <DateWindowFields {...dates} />
            <FilterField label={tr('billing', 'source')}>
              <Select className="w-56" value={sourceType} onChange={(e) => setSourceType(e.target.value)}>
                <option value="">{tr('billing', 'all_sources')}</option>
                {['invoice', 'invoice_adjustment', 'payment', 'refund', 'customer_credit', 'grn', 'claim', 'payroll', 'doctor_payout', 'doctor_payout_paid', 'manual', 'reversal'].map((s) => (
                  <option key={s} value={s}>
                    {humanize(s)}
                  </option>
                ))}
              </Select>
            </FilterField>
          </>
        }
        trailing={
          <a
            href={`/api/v1/accounting/export/journal.csv${range(from, to)}`}
            className="inline-flex h-8 items-center gap-2 rounded-md border px-3 text-sm font-medium focus-ring"
          >
            <Download className="h-4 w-4" /> {tr('billing', 'export_csv')}
          </a>
        }
        onReset={reset}
      />
      <PeriodLockBar />

      <div className="space-y-3">
        {journals.data?.map((entry) => (
          <Card key={entry.id}>
            <CardHeader className="flex-row items-center justify-between">
              <div>
                <CardTitle className="text-sm">
                  {entry.number} <span className="font-normal text-muted-foreground">· {dateOnly(entry.entryDate)}</span>
                </CardTitle>
                <p className="text-xs text-muted-foreground">{entry.memo}</p>
              </div>
              <Badge variant="secondary">{humanize(entry.sourceType ?? 'manual')}</Badge>
            </CardHeader>
            <CardContent>
              <DataTable
                dense
                rows={entry.lines}
                rowKey={(line) => line.id}
                columns={[
                  { header: tr('billing', 'account'), sort: (line) => line.account.code, cell: (line) => `${line.account.code} · ${line.account.name}` },
                  { header: tr('billing', 'memo'), sort: (line) => line.memo, cell: (line) => line.memo ?? '' },
                  { header: tr('billing', 'debit'), align: 'right', sort: (line) => line.debitMinor, cell: (line) => (line.debitMinor ? money(line.debitMinor) : '') },
                  { header: tr('billing', 'credit'), align: 'right', sort: (line) => line.creditMinor, cell: (line) => (line.creditMinor ? money(line.creditMinor) : '') },
                ]}
              />
            </CardContent>
          </Card>
        ))}
        {journals.data?.length === 0 && (
          <div className="rounded-lg border p-10 text-center text-sm text-muted-foreground">
            {tr('billing', 'nothing_posted_in_this_window')}
          </div>
        )}
      </div>
    </>
  );
}

function AccountsTab() {
  const accounts = useQuery({
    queryKey: ['accounts', 'all'],
    queryFn: () => get<Account[]>('/accounting/accounts?includeInactive=true'),
  });

  return (
    <DataTable
      rows={accounts.data}
      loading={accounts.isLoading} refreshing={accounts.isFetching}
      error={accounts.error}
      rowKey={(row) => row.id}
      empty={tr('billing', 'no_chart_of_accounts')}
      columns={[
        { header: tr('billing', 'code'), sort: (row) => row.code, cell: (row) => <span className="font-mono text-xs">{row.code}</span> },
        {
          header: tr('billing', 'name'),
          sort: (row) => row.name,
          cell: (row) => <span className={row.isPostable ? '' : 'font-semibold'}>{row.name}</span>,
        },
        { header: tr('billing', 'type'), sort: (row) => row.type, cell: (row) => <Badge variant="secondary">{humanize(row.type, 'account_type')}</Badge> },
        { header: tr('billing', 'postable'), sort: (row) => row.isPostable, cell: (row) => (row.isPostable ? 'Yes' : 'Heading') },
        { header: tr('billing', 'active'), sort: (row) => row.isActive, cell: (row) => (row.isActive ? 'Yes' : 'No') },
      ]}
    />
  );
}
